// client/src/pages/OpenHouseSignInPage.js — the tablet at the door. Public: the token in
// the URL is the only authority. Large touch targets; the form resets itself after each
// visitor so the next person never sees the last one's details.
import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

const EMPTY = { first_name: '', last_name: '', email: '', phone: '', has_agent: '', buyer_agent_name: '', consents: {}, website: '' };
const RESET_MS = 6000;

const OpenHouseSignInPage = () => {
    const { token } = useParams();
    const [info, setInfo] = useState(null);
    const [f, setF] = useState(EMPTY);
    const [error, setError] = useState('');
    const [thanks, setThanks] = useState('');
    const [busy, setBusy] = useState(false);
    const timer = useRef(null);

    useEffect(() => {
        fetch(`/api/open-houses/public/${encodeURIComponent(token)}`)
            .then(async (r) => { const j = await r.json(); if (!r.ok) throw new Error(j.message); setInfo(j); })
            .catch((e) => setError(e.message || 'This sign-in page is not valid.'));
        return () => clearTimeout(timer.current);
    }, [token]);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true); setError('');
        try {
            const r = await fetch(`/api/open-houses/public/${encodeURIComponent(token)}/sign-in`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...f, has_agent: f.has_agent === 'yes' }),
            });
            const j = await r.json();
            if (!r.ok) throw new Error(j.message);
            setThanks(j.message); setF(EMPTY);
            timer.current = setTimeout(() => setThanks(''), RESET_MS);
        } catch (err) { setError(err.message); } finally { setBusy(false); }
    };

    const big = 'w-full p-4 text-lg border-2 rounded-lg focus:border-slate-500 outline-none';
    const choice = (v) => `flex-1 py-4 text-lg rounded-lg border-2 ${f.has_agent === v ? 'border-slate-700 bg-indigo-50 font-semibold' : 'border-slate-300'}`;
    const toggle = (k) => setF({ ...f, consents: { ...f.consents, [k]: !f.consents[k] } });

    return (
        <div className="min-h-screen bg-slate-100 flex items-start justify-center p-4">
            <div className="bg-white rounded-2xl shadow-lg max-w-2xl w-full p-6 md:p-10">
                <p className="text-sm uppercase tracking-wide text-indigo-700 font-semibold">{info?.brokerage || 'Open house'}</p>
                <h1 className="text-3xl font-bold text-slate-800 mt-1">Welcome{info?.address ? ` to ${info.address}` : ''}</h1>
                {info?.host_name && <p className="text-slate-600 mt-1">Hosted by {info.host_name}</p>}
                {error && <p className="mt-4 text-red-700 text-lg">{error}</p>}
                {info && !info.open && <p className="mt-6 text-lg text-slate-700">{info.closed_reason}</p>}
                {thanks && <p className="mt-8 text-2xl text-green-700 text-center">{thanks}</p>}
                {info?.open && !thanks && (
                    <form onSubmit={submit} className="mt-6 space-y-4" autoComplete="off">
                        <div className="grid md:grid-cols-2 gap-3">
                            <input required className={big} placeholder="First name" value={f.first_name} onChange={(e) => setF({ ...f, first_name: e.target.value })} />
                            <input className={big} placeholder="Last name" value={f.last_name} onChange={(e) => setF({ ...f, last_name: e.target.value })} />
                            <input type="email" inputMode="email" className={big} placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
                            <input type="tel" inputMode="tel" className={big} placeholder="Phone" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} />
                        </div>
                        <input type="text" tabIndex={-1} className="hidden" aria-hidden="true" value={f.website} onChange={(e) => setF({ ...f, website: e.target.value })} />
                        <div>
                            <p className="text-lg font-medium text-slate-800 mb-2">Are you working with a real estate agent?</p>
                            <div className="flex gap-3">
                                <button type="button" className={choice('yes')} onClick={() => setF({ ...f, has_agent: 'yes', consents: {} })}>Yes</button>
                                <button type="button" className={choice('no')} onClick={() => setF({ ...f, has_agent: 'no' })}>No</button>
                            </div>
                        </div>
                        {f.has_agent === 'yes' && (
                            <input className={big} placeholder="Your agent's name (optional)" value={f.buyer_agent_name} onChange={(e) => setF({ ...f, buyer_agent_name: e.target.value })} />
                        )}
                        {f.has_agent === 'no' && info.consents && (
                            <div className="space-y-3 border-t pt-4">
                                <p className="text-sm text-slate-500">Optional — tick only what you want.</p>
                                {[['calls_texts', f.phone], ['email_updates', f.email]].map(([k, have]) => (
                                    <label key={k} className={`flex items-start gap-3 p-3 rounded-lg border ${have ? '' : 'opacity-50'}`}>
                                        <input type="checkbox" className="mt-1 w-6 h-6" disabled={!have} checked={!!f.consents[k]} onChange={() => toggle(k)} />
                                        <span className="text-sm text-slate-700">{info.consents[k]}</span>
                                    </label>
                                ))}
                            </div>
                        )}
                        <button disabled={busy || !f.has_agent} className="w-full py-4 text-xl bg-slate-800 text-white rounded-lg font-semibold disabled:opacity-50">Sign in</button>
                        <p className="text-xs text-slate-400 text-center">Your details go only to {info.brokerage}. We do not sell or share them.</p>
                    </form>
                )}
            </div>
        </div>
    );
};

export default OpenHouseSignInPage;
